import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import { keys } from "@/api/queryKeys";
import type {
  AnthropicAccountSummary,
  GithubAccountSummary,
  GithubDevicePoll,
  GithubDeviceStart,
  OpenAIAccountSummary,
} from "@/api/types";

export function useOpenAIAccounts() {
  return useQuery({
    queryKey: keys.openaiAccounts,
    queryFn: async () =>
      (await api.get<OpenAIAccountSummary[]>("/openai-link/accounts")).data,
  });
}

export function useLinkApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { display_name: string; api_key: string }) =>
      (await api.post<OpenAIAccountSummary>("/openai-link/api-key", payload))
        .data,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.openaiAccounts }),
  });
}

export type OAuthStart = {
  handle_id: string;
  authorize_url: string;
};

export function useStartOAuth() {
  return useMutation({
    mutationFn: async (payload: { display_name: string }) =>
      (await api.post<OAuthStart>("/openai-link/oauth/start", payload)).data,
  });
}

export function useExchangeOAuth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { handle_id: string; pasted: string }) =>
      (
        await api.post<OpenAIAccountSummary>(
          "/openai-link/oauth/exchange",
          payload,
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.openaiAccounts }),
  });
}

/** Kick off a re-auth flow against an existing account. Same PKCE
 *  dance as useStartOAuth, but the backend stamps target_account_id
 *  on the pending entry so exchange writes back to this row instead
 *  of creating a new one. */
export function useStartReauthOAuth() {
  return useMutation({
    mutationFn: async (account_id: string) =>
      (
        await api.post<OAuthStart & { target_account_id: string }>(
          `/openai-link/accounts/${account_id}/reauth/start`,
          {},
        )
      ).data,
  });
}

/** Complete a re-auth flow — writes the fresh tokens back to the
 *  existing account row. */
export function useExchangeReauthOAuth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      account_id: string;
      handle_id: string;
      pasted: string;
    }) =>
      (
        await api.post<OpenAIAccountSummary>(
          `/openai-link/accounts/${payload.account_id}/reauth/exchange`,
          { handle_id: payload.handle_id, pasted: payload.pasted },
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.openaiAccounts }),
  });
}

// ----- Anthropic / Claude account-link hooks -------------------------
//
// Mirror the OpenAI hooks above one-for-one: same query-key layout
// (``["anthropic", "accounts"]``), same mutation→invalidate pattern,
// same response shapes. The two providers diverge on URL prefix
// (``/anthropic-link/...``) and the backend's central-billing flag
// name (``central_billing`` vs ``server_billing_available``); every
// other contract is intentionally identical so the
// ``<AccountRow>`` presentation can stay provider-agnostic.

export function useAnthropicAccounts() {
  return useQuery({
    queryKey: keys.anthropicAccounts,
    queryFn: async () =>
      (await api.get<AnthropicAccountSummary[]>("/anthropic-link/accounts"))
        .data,
  });
}

export function useLinkAnthropicApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { display_name: string; api_key: string }) =>
      (
        await api.post<AnthropicAccountSummary>(
          "/anthropic-link/api-key",
          payload,
        )
      ).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: keys.anthropicAccounts }),
  });
}

export function useStartAnthropicOAuth() {
  return useMutation({
    mutationFn: async (payload: { display_name: string }) =>
      (await api.post<OAuthStart>("/anthropic-link/oauth/start", payload)).data,
  });
}

export function useExchangeAnthropicOAuth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { handle_id: string; pasted: string }) =>
      (
        await api.post<AnthropicAccountSummary>(
          "/anthropic-link/oauth/exchange",
          payload,
        )
      ).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: keys.anthropicAccounts }),
  });
}

/** Re-auth start for Anthropic. See ``useStartReauthOAuth`` for the
 *  general shape — same PKCE-handle dance, just hitting the
 *  ``/anthropic-link`` prefix. */
export function useStartAnthropicReauth() {
  return useMutation({
    mutationFn: async (account_id: string) =>
      (
        await api.post<OAuthStart & { target_account_id: string }>(
          `/anthropic-link/accounts/${account_id}/reauth/start`,
          {},
        )
      ).data,
  });
}

export function useExchangeAnthropicReauth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      account_id: string;
      handle_id: string;
      pasted: string;
    }) =>
      (
        await api.post<AnthropicAccountSummary>(
          `/anthropic-link/accounts/${payload.account_id}/reauth/exchange`,
          { handle_id: payload.handle_id, pasted: payload.pasted },
        )
      ).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: keys.anthropicAccounts }),
  });
}

export function useUnlinkAnthropicAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/anthropic-link/accounts/${id}`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: keys.anthropicAccounts }),
  });
}

/** Hard-delete a revoked Anthropic account row. Mirrors
 *  ``usePurgeAccount`` for OpenAI — service rejects with 409 if the
 *  row is still ``active``, so the usual flow is unlink → purge. */
export function usePurgeAnthropicAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/anthropic-link/accounts/${id}/purge`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: keys.anthropicAccounts }),
  });
}

/** Whether the deployment exposes a server-wide Anthropic API key.
 *  Field name (``central_billing``) differs from the OpenAI side
 *  (``server_billing_available``) on purpose — matches the backend's
 *  /api/anthropic-link/billing payload. */
export function useAnthropicCentralBilling() {
  return useQuery({
    queryKey: ["anthropic-link", "billing"],
    queryFn: async () =>
      (
        await api.get<{ central_billing: boolean }>(
          "/anthropic-link/billing",
        )
      ).data,
  });
}

export function useServerBilling() {
  return useQuery({
    queryKey: ["openai-link", "billing"],
    queryFn: async () =>
      (
        await api.get<{ server_billing_available: boolean }>(
          "/openai-link/billing",
        )
      ).data,
  });
}

// ----- GitHub Copilot account-link hooks ------------------------------
//
// Same query-key layout as the OpenAI / Anthropic siblings (``["github",
// "accounts"]``), same mutation→invalidate pattern. The OAuth flow
// diverges from the other two providers: Copilot uses GitHub's
// device-flow (mirrors ``gh auth login --device``), so the SPA polls
// ``/oauth/device/poll`` rather than collecting a pasted code. The
// API-key surface accepts ``gho_*`` / ``ghu_*`` GitHub OAuth tokens
// only — Personal Access Tokens are rejected by the backend up-front
// because the Copilot exchange would 404 on them.

export function useGithubAccounts() {
  return useQuery({
    queryKey: keys.githubAccounts,
    queryFn: async () =>
      (await api.get<GithubAccountSummary[]>("/github-link/accounts")).data,
  });
}

export function useLinkGithubApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      display_name: string;
      github_token: string;
    }) =>
      (
        await api.post<GithubAccountSummary>(
          "/github-link/api-key",
          payload,
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.githubAccounts }),
  });
}

export function useStartGithubDevice() {
  return useMutation({
    mutationFn: async (payload: { display_name: string }) =>
      (
        await api.post<GithubDeviceStart>(
          "/github-link/oauth/device/start",
          payload,
        )
      ).data,
  });
}

export function usePollGithubDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { handle_id: string }) =>
      (
        await api.post<GithubDevicePoll>(
          "/github-link/oauth/device/poll",
          payload,
        )
      ).data,
    onSuccess: (data) => {
      // Invalidate only on a terminal state — pending polls don't
      // change the account list and re-fetching every 5s would
      // hammer the GitHub list endpoint.
      if (data.state === "ok") {
        void qc.invalidateQueries({ queryKey: keys.githubAccounts });
      }
    },
  });
}

export function useStartGithubReauthDevice() {
  return useMutation({
    mutationFn: async (account_id: string) =>
      (
        await api.post<GithubDeviceStart>(
          `/github-link/accounts/${account_id}/reauth/device/start`,
          {},
        )
      ).data,
  });
}

export function usePollGithubReauthDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      account_id: string;
      handle_id: string;
    }) =>
      (
        await api.post<GithubDevicePoll>(
          `/github-link/accounts/${payload.account_id}/reauth/device/poll`,
          { handle_id: payload.handle_id },
        )
      ).data,
    onSuccess: (data) => {
      if (data.state === "ok") {
        void qc.invalidateQueries({ queryKey: keys.githubAccounts });
      }
    },
  });
}

export function useUnlinkGithubAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/github-link/accounts/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.githubAccounts }),
  });
}

export function usePurgeGithubAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/github-link/accounts/${id}/purge`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.githubAccounts }),
  });
}

export function useGithubCentralBilling() {
  return useQuery({
    queryKey: ["github-link", "billing"],
    queryFn: async () =>
      (
        await api.get<{ central_billing: boolean }>(
          "/github-link/billing",
        )
      ).data,
  });
}

export type CodexUsageWindow = {
  turns: number;
  total_tokens: number;
  cost_usd_micros: number;
};

export type CodexUsageResponse = {
  as_of: string;
  windows: Record<"5h" | "7d", CodexUsageWindow>;
};

/** Self-measured rolling-window Codex usage (no OpenAI quota API
 *  exposed for Codex subs, so this is our best gut-check). Refresh on
 *  a modest interval so the composer's chip updates after a turn
 *  ends without waiting for a full page load. */
export function useCodexUsage() {
  return useQuery({
    queryKey: ["openai-link", "usage"],
    queryFn: async () =>
      (await api.get<CodexUsageResponse>("/openai-link/usage")).data,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useUnlinkAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/openai-link/accounts/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.openaiAccounts }),
  });
}

/** Choose which linked OpenAI account runs the user's turns. Applies
 *  from their next turn; the list refetches so the "in use" mark moves. */
export function useSetPreferredOpenAIAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<OpenAIAccountSummary>(`/openai-link/accounts/${id}/preferred`))
        .data,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.openaiAccounts }),
  });
}

/** Hard-delete a revoked OpenAI account row. Distinct from
 *  ``useUnlinkAccount`` which soft-revokes a live account; this
 *  call only succeeds when the row is already in ``revoked``
 *  state. Sessions that still point at the account get their
 *  openai_account_id nulled server-side and fall back to
 *  central billing on their next turn. */
export function usePurgeAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/openai-link/accounts/${id}/purge`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.openaiAccounts }),
  });
}
